/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ANALYTICS_PROVIDERS_DECLARED } from '../plugin-manager/first-party-plugins.generated'
import {
  applyAnalyticsConsent,
  type AnalyticsProviderDeclaration,
} from './analytics-provider'

/**
 * Visitor tracking consent (AGL-1498): the single answer to "may this
 * tracking feature run for this visitor?", shared by every surface that has
 * to agree about it — the tenant's GA injection, the consent banner, the
 * marketing runtime's visitor id, the console card that configures it, and
 * the console preview's region simulator.
 *
 * The model, and the honesty bar it was built against:
 *
 * - **Strictly necessary — no consent asked, none required.** The cart cookie
 *   (`aglyn_cart_{hostId}`, commerce checkout), the member session
 *   (`aglyn_member_{hostId}`, memberships sign-in), the `aglyn-tenant-host`
 *   preview-host override, popup/announcement dismissal stamps (local-only,
 *   never transmitted, and blocking them would show MORE popups), the
 *   first-party pageview beacon (cookieless, stores no visitor identifier —
 *   AGL-82), and the stored consent choice itself.
 * - **Consent-gated: `advertising`** (AGL-1649) — GA4's `ad_storage`,
 *   `ad_user_data` and `ad_personalization`. OFF for every site unless the
 *   host turns the question on, and granted only by an explicit visitor yes
 *   to that specific category: never by the implied default, never by a
 *   record written before the category existed, never by any refusal status.
 *   A host who needs Ads storage previously had one workaround — switch the
 *   whole tool off and run their own CMP — which is why this exists.
 * - **Consent-gated: `analytics`.** Today that is the customer-configured
 *   Google Analytics tag — a third-party identifier-setting script — plus
 *   the CROSS-VISIT persistence of the `aglyn:visitor` experiment id (the id
 *   never leaves the browser, but an indefinite-lifetime identifier is
 *   disproportionate without a basis; without one it degrades to
 *   sessionStorage, so variants stay stable within the visit).
 *
 * **Two postures, resolved per visitor** (the Squarespace shape, host-chosen):
 *
 * - **`opt-in`** — prior consent: nothing gated loads until an explicit
 *   accept. Applied in regions whose law requires prior consent (EU/EEA/UK),
 *   whenever the visitor's region is UNKNOWN, and everywhere when the host
 *   picks `strict` mode. Unknown-geo falls to opt-in deliberately: the
 *   asymmetry is a few lost analytics events on rare headerless visits
 *   versus pre-consent tracking of an EU visitor.
 * - **`opt-out`** — implied consent: gated features are live from first
 *   paint, no banner and no notice; the visitor's recorded state is
 *   `implied`, and the always-present "Your Privacy Choices" control is the
 *   opt-out surface. Applied outside prior-consent regions when the host
 *   mode is `geo`.
 *
 * **GPC (Global Privacy Control)** is honored as an automatic opt-out in
 * both postures: a visitor whose browser sends it is recorded `gpc-opt-out`
 * and never tracked, unless they later explicitly accept (the signal is a
 * default; a specific, informed choice overrides it).
 *
 * Enforcement is AT THE SOURCE and MODE-INDEPENDENT: whenever the verdict is
 * "no", the GA script never loads — not load-then-suppress — and the gate
 * does not care WHY consent is absent. Because tenant pages are ISR-cached,
 * both the posture and the verdict are evaluated client-side only — the
 * cached HTML never varies by region or consent state.
 */

/** The host fields this module reads; keeps callers free of the full doc. */
export interface VisitorConsentHost {
  analytics?: {
    gaMeasurementId?: string
    /** GTM container (AGL-2486) — gated exactly as the GA id is. */
    gtmContainerId?: string
  } | null
  consent?: {
    /**
     * Host opt-out of the whole tool (AGL-1498). PERSISTED NAME. `true`
     * means the host runs their own consent solution (or accepts the
     * exposure): no banner, no privacy-choices control, and gated features
     * load for every visitor. Absent means the tool is active.
     */
    disabled?: boolean
    /**
     * The host's consent posture (AGL-1498). PERSISTED NAME.
     * - `'geo'` — geo-conditional: implied consent (tracking on, opt-out
     *   offered) outside prior-consent regions; a prior-consent banner in
     *   EU/EEA/UK and wherever the region is unknown.
     * - `'strict'` — opt-in everywhere: every visitor gets the banner.
     * Absent behaves as `'geo'` — the field practice (Squarespace) and the
     * posture the console preselects; recorded here so nobody discovers it
     * by surprise.
     */
    mode?: 'geo' | 'strict'
    /**
     * Host opt-in to ASKING about advertising storage (AGL-1649). PERSISTED
     * NAME. Absent or `false` — the default for every site that exists — means
     * the visitor is asked about analytics only and the three advertising
     * signals stay denied, which is where AGL-1622 put them.
     *
     * `true` adds a SECOND question to the banner and the privacy-choices
     * panel. It does not grant anything by itself: it creates the opportunity
     * for a visitor to say yes, which is the thing a host previously could not
     * obtain without switching the whole tool off and running their own CMP.
     *
     * Strictly `=== true`, like {@link isConsentToolDisabled}: a truthy string
     * out of a hand-edited host document must not turn a consent category on.
     */
    advertising?: boolean
  } | null
}

/** The host's configured posture; absent means `'geo'`. */
export type HostConsentMode = 'geo' | 'strict'

/** The per-visitor resolved posture. */
export type VisitorConsentPosture = 'opt-in' | 'opt-out'

/**
 * Consent-gated feature categories.
 *
 * `advertising` (AGL-1649) is gated ONLY where the host opted in — see
 * {@link hostAsksAboutAdvertising}. The stored shape is keyed by category, so
 * this extended rather than migrated, and a record written before it existed
 * reads as "never asked", never as a grant.
 */
export type VisitorConsentCategory = 'analytics' | 'advertising'

/**
 * A visitor's recorded consent state. The five are DISTINCT on purpose:
 * "how many visitors are tracked" (and any future regulator question) needs
 * to distinguish a visitor who clicked Accept from one who was defaulted in.
 *
 * - `implied` — opt-out posture, no action taken: tracking is on.
 * - `accepted` — explicit yes (either posture): tracking is on.
 * - `declined` — explicit no to a prior-consent banner: tracking is off.
 * - `opted-out` — explicit opt-out from the implied default: tracking off.
 * - `gpc-opt-out` — the browser's GPC signal, honored: tracking off.
 */
export type VisitorConsentStatus =
  | 'implied'
  | 'accepted'
  | 'declined'
  | 'opted-out'
  | 'gpc-opt-out'

const CONSENT_STATUSES: ReadonlySet<string> = new Set([
  'implied',
  'accepted',
  'declined',
  'opted-out',
  'gpc-opt-out',
])

/** The statuses that are a visitor's own click, not a default or a signal. */
export function isExplicitConsentStatus(
  status: VisitorConsentStatus | null | undefined,
): boolean {
  return (
    status === 'accepted' || status === 'declined' || status === 'opted-out'
  )
}

/**
 * Which refusal a "no" is, for a visitor in this state.
 *
 * `opted-out` when they were defaulted in, `declined` when they were asked
 * first. The GATE does not care — both refuse — but the RECORD does: "how many
 * visitors were tracked before they said no" is a question only these two
 * answers separate, and it is a question a regulator can ask.
 *
 * Lives here rather than in either surface that needs it. Both the consent
 * overlay and the console's own module were deriving it, and a rule with two
 * derivations is a rule that eventually means two different things.
 */
export function refusalStatusFor(
  stored: { status?: VisitorConsentStatus } | null | undefined,
  posture: VisitorConsentPosture | null | undefined,
): VisitorConsentStatus {
  return stored?.status === 'implied' || posture === 'opt-out'
    ? 'opted-out'
    : 'declined'
}

/** Whether a status grants the analytics category. */
export function analyticsGrantedByStatus(
  status: VisitorConsentStatus,
): boolean {
  return status === 'accepted' || status === 'implied'
}

/**
 * Whether a status can carry an advertising grant at all (AGL-1649; widened by
 * AGL-2402, narrowed back on 2026-08-24, widened again on 2026-08-25).
 *
 * The SAME status set as {@link analyticsGrantedByStatus}, and the agreement is
 * deliberate: `implied` grants both. Being defaulted in where the law permits a
 * default is one posture, applied to both categories, and the visitor's opt-out
 * surface is the same one.
 *
 * ## Why it moved three times, because the next person will move it again
 *
 * AGL-2402 (`a410d8785`) first made `implied` qualify, on the reasoning that
 * the rule is safe by GEOGRAPHY: an `implied` record can only ever be written
 * in the OPT-OUT posture, because {@link resolveConsentPosture} puts the EU 27,
 * the EEA/EFTA three, the UK, Gibraltar, the EU outermost regions AND any
 * visitor whose region cannot be determined into opt-in, where
 * `decideVisitorConsent` records no default at all. That half was always sound
 * and is still asserted in `visitor-consent-advertising.spec.ts`.
 *
 * The half that failed was DISCLOSURE. That commit stated the published Cookie
 * Policy had been updated first; read against the live page on 2026-08-24 it
 * said two different things — its "Marketing / advertising" paragraph described
 * the opt-out posture, while the per-cookie table said `_gac`, `_gcl_au`,
 * `_fbp` and `_fbc` were "set only where you have allowed advertising cookies"
 * and "Your choices" repeated it. A policy that says both cannot authorise the
 * wider behaviour, so the code was narrowed to the strictest thing it said.
 *
 * On 2026-08-25 the master moved for real, in the right order: those five
 * opt-in-only sentences were rewritten in the Cookie Policy gdoc, and only then
 * did this function follow. The Privacy Policy needed nothing — it already said
 * *"With your consent, we do 'share' … for cross-context behavioral
 * advertising"*, named Google and Meta, and described the EU/UK-ask-first vs.
 * elsewhere-from-first-visit split. The flat "we do not sell or share" denial
 * that the narrowing reasoned from was a paraphrase in `docs/ANALYTICS.md`, not
 * a sentence in the document.
 *
 * ⛔ Re-narrowing is a POLICY act, not a code change. Move the published
 * masters first and let this follow — never the other way round. Nine surfaces
 * describe this rule to a human and `consent-advertising-copy-drift.spec.ts`
 * fails on all of them the moment the two disagree.
 *
 * A `true` here is necessary, never sufficient: the host must ALSO have opted
 * into asking (see {@link hostAsksAboutAdvertising}), and a refusal — declined,
 * opted out, or a GPC signal — outranks the default everywhere.
 */
export function advertisingGrantedByStatus(
  status: VisitorConsentStatus,
): boolean {
  // Fails CLOSED: an exact match against the granting statuses, so any
  // unknown, absent or future status answers `false`. `strictNullChecks` is
  // off repo-wide, which means `status` can be `null`/`undefined` at runtime
  // despite the type — an equality test denies those, an exclusion list
  // (`status !== 'declined' && …`) would have granted them.
  //
  // `implied` GRANTS from 2026-08-25, and the policy went first (the call,
  // executed in that order). `decideVisitorConsent` only ever writes `implied`
  // in the OPT-OUT posture — `resolveConsentPosture` keeps the EU 27, the
  // EEA/EFTA three, the UK, Gibraltar, the EU outermost regions AND any visitor
  // whose region cannot be determined in opt-in, where no default is recorded at
  // all — so this widens advertising to the rest of the world only, and the
  // geographic guarantee `visitor-consent-advertising.spec.ts` asserts is
  // untouched. `gpc-opt-out` and `declined` still answer `false`.
  //
  // The disclosure this rests on is now consistent in BOTH masters, which is
  // what the 2026-08-24 narrowing was waiting for: the Privacy Policy already
  // said *"With your consent, we do 'share' … Where the law requires us to ask
  // first — the EU, the UK, and anywhere we cannot determine your region — it
  // does not load until you accept. Elsewhere it runs from your first visit"*,
  // and the Cookie Policy's two remaining opt-in-only sentences (the ×4
  // per-cookie note and the "Your choices" bullet) were rewritten to match it
  // the same day. Re-narrow this ONLY by moving the policies back first.
  return status === 'accepted' || status === 'implied'
}

/**
 * Strict GA measurement-id check (AGL-138) — the id lands inside an inline
 * script, so the format gate is load-bearing, not cosmetic.
 */
export const GA_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{4,16}$/

/**
 * The shape a Meta pixel id has, shared by the LOADER and the console field.
 *
 * Declared HERE and not in `advertising-tags.ts`, which the console may not
 * import: `advertising-tag-gate.spec` case (f) fails any console file that
 * reaches for that module, because it is what mounts a vendor's script. A
 * format string is not a vendor, so it lives with the other id patterns and
 * the loader imports it from here.
 *
 * One pattern, because two would be a form that accepts an id the loader then
 * refuses — the tag never mounts and the operator is left looking at a saved
 * field and no pixel.
 */
export const META_PIXEL_ID_PATTERN = /^[0-9]{8,20}$/

/**
 * A Google Ads conversion id — `AW-` and digits.
 *
 * Distinct from `GA_MEASUREMENT_ID_PATTERN` (`G-…`) on purpose: they are
 * different products with different ids, and a form that accepted either in
 * either field would load an analytics id into an ads tag and report nothing.
 */
export const GOOGLE_ADS_ID_PATTERN = /^AW-[0-9]{6,16}$/

/**
 * A LinkedIn Insight partner id — numeric, and short.
 *
 * Bounded well under the Meta band rather than sharing it: a partner id is six
 * or seven digits today, and a pattern loose enough to accept a Meta pixel id
 * would let a mis-paste into the wrong field validate and then load a tag that
 * matches nothing.
 */
export const LINKEDIN_PARTNER_ID_PATTERN = /^[0-9]{4,10}$/

/** The configured GA id when it is well-formed, else null. */
export function resolveGaMeasurementId(
  host: VisitorConsentHost | null | undefined,
): string | null {
  const candidate = String(host?.analytics?.gaMeasurementId ?? '')
  return GA_MEASUREMENT_ID_PATTERN.test(candidate) ? candidate : null
}

/**
 * A Google Tag Manager container id — `GTM-` and 5–10 upper-case
 * alphanumerics.
 *
 * Format-checked for the same reason the GA id is: it lands inside an INLINE
 * SCRIPT on a published page, so anything that is not this shape must never
 * reach it (AGL-138).
 */
export const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]{5,10}$/

/** The configured GTM container when it is well-formed, else null. */
export function resolveGtmContainerId(
  host: VisitorConsentHost | null | undefined,
): string | null {
  const candidate = String(host?.analytics?.gtmContainerId ?? '')
  return GTM_CONTAINER_ID_PATTERN.test(candidate) ? candidate : null
}

/**
 * The site's analytics settings that can configure a tag, and the shape each
 * must have to reach a page — the same patterns the console's Tracking tab
 * validates with. A provider declares which of these it mounts a tag for
 * (`analyticsProvider.settings` in `plugins.config.json`); a declared setting
 * with no shape here configures nothing.
 */
export const ANALYTICS_SETTING_PATTERNS: Readonly<Record<string, RegExp>> =
  Object.freeze({
    gaMeasurementId: GA_MEASUREMENT_ID_PATTERN,
    gtmContainerId: GTM_CONTAINER_ID_PATTERN,
  })

/**
 * Whether the site runs ANY analytics tag a provider mounts — a measurement
 * id, a tag container, or both (AGL-2486).
 *
 * The one predicate both consent questions are asked through, because the
 * alternative is two places that each remember only one of the settings. A
 * site with a container and no measurement id is a site that tracks: it was
 * the shape that would have had `consentGatedCategories` return `[]`, no
 * banner rendered, and the container loading ungated — which for a container
 * is worse than for a single tag, since a container is exactly the thing that
 * can carry advertising tags.
 *
 * Read from the COMPILED provider declarations, never from which adapters
 * have loaded: this is asked during render on the published page and in the
 * console, before any adapter could have arrived, and "no tag" would mean no
 * banner in front of one. With no provider declared, nothing can mount, and
 * the answer is `false`.
 */
export function hostConfiguresAnalyticsTag(
  host: VisitorConsentHost | null | undefined,
  declared: readonly AnalyticsProviderDeclaration[] = ANALYTICS_PROVIDERS_DECLARED,
): boolean {
  const settings = (host?.analytics ?? null) as Record<string, unknown> | null
  if (!settings) return false
  for (const provider of declared) {
    for (const field of provider.settings) {
      const pattern = ANALYTICS_SETTING_PATTERNS[field]
      if (pattern && pattern.test(String(settings[field] ?? ''))) return true
    }
  }
  return false
}

/** Host opt-out of the tool. Absent means ACTIVE — the safe default. */
export function isConsentToolDisabled(
  host: VisitorConsentHost | null | undefined,
): boolean {
  return host?.consent?.disabled === true
}

/** The host's posture choice; anything but `'strict'` reads as `'geo'`. */
export function resolveHostConsentMode(
  host: VisitorConsentHost | null | undefined,
): HostConsentMode {
  return host?.consent?.mode === 'strict' ? 'strict' : 'geo'
}

/**
 * The gated categories this site actually uses. Empty means there is
 * nothing to consent to and no consent UI renders — a banner with no
 * question behind it is decoration that trains visitors to click banners.
 */
export function consentGatedCategories(
  host: VisitorConsentHost | null | undefined,
): VisitorConsentCategory[] {
  // A CONTAINER counts (AGL-2486). Read as the measurement id alone, a site
  // running only a container had no gated category, so no banner rendered
  // and the container loaded ungated — see `hostConfiguresAnalyticsTag`.
  if (!hostConfiguresAnalyticsTag(host)) return []
  return hostAsksAboutAdvertising(host)
    ? ['analytics', 'advertising']
    : ['analytics']
}

/**
 * Whether this site asks its visitors about advertising storage (AGL-1649).
 *
 * Two conditions, both required. The host has to have opted in — the flag is
 * off for every site that exists and stays off unless someone turns it on —
 * and the site has to actually have an analytics tag, because advertising
 * storage here is GA4's `ad_storage` and there is nothing to ask about
 * without one. A banner with no question behind it is decoration (AGL-1498).
 */
export function hostAsksAboutAdvertising(
  host: VisitorConsentHost | null | undefined,
): boolean {
  // Either tag qualifies (AGL-2486): advertising storage is what a
  // measurement tag reads and what a container's advertising tags read, and
  // a container is the likelier of the two to carry them.
  return host?.consent?.advertising === true && hostConfiguresAnalyticsTag(host)
}

/**
 * Whether the published site runs the consent machinery at all: the tool is
 * active AND the site uses at least one gated feature.
 */
export function hostConsentRequired(
  host: VisitorConsentHost | null | undefined,
): boolean {
  return !isConsentToolDisabled(host) && consentGatedCategories(host).length > 0
}

/**
 * Regions whose law requires PRIOR consent for non-essential
 * cookies/identifiers (ePrivacy and its UK retention): the EU 27, the three
 * EEA/EFTA states, the UK, Gibraltar (UK GDPR extends there), and the EU
 * outermost regions that carry their own ISO codes. ISO 3166-1 alpha-2,
 * matching `x-vercel-ip-country`.
 *
 * Deliberately NOT a "privacy-law countries" list: plenty of laws (CCPA,
 * LGPD, PIPEDA) regulate tracking on an opt-out basis, which is exactly what
 * the opt-out posture plus GPC provides. This set is only the prior-consent
 * boundary.
 */
export const PRIOR_CONSENT_COUNTRY_CODES: ReadonlySet<string> = new Set([
  // EU 27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
  // EEA / EFTA
  'IS', 'LI', 'NO',
  // UK and Gibraltar
  'GB', 'GI',
  // EU outermost regions with their own ISO codes (legally EU territory)
  'AX', 'GF', 'GP', 'MQ', 'RE', 'YT',
])

/**
 * The per-visitor posture. Client-evaluated (ISR forbids varying the HTML),
 * from the country the region endpoint reports.
 *
 * Unknown region (`null`) falls to `opt-in` — the ONE place this feature
 * does not maximize tracking, because the asymmetry is lopsided: a few lost
 * analytics events on rare headerless visits, versus pre-consent tracking
 * of an EU visitor, which "track as soon as we legally can" excludes by its
 * own terms.
 */
export function resolveConsentPosture(
  host: VisitorConsentHost | null | undefined,
  country: string | null | undefined,
): VisitorConsentPosture {
  if (resolveHostConsentMode(host) === 'strict') return 'opt-in'
  if (!country) return 'opt-in'
  return PRIOR_CONSENT_COUNTRY_CODES.has(country.toUpperCase())
    ? 'opt-in'
    : 'opt-out'
}

/**
 * Whether this browser sends Global Privacy Control. Read live at decision
 * time — the signal is the user agent's, not ours to cache.
 */
export function hasGlobalPrivacyControl(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    (navigator as { globalPrivacyControl?: unknown }).globalPrivacyControl ===
      true
  )
}

/**
 * A visitor's stored consent record. Versioned so a future category
 * addition can distinguish "declined analytics" from "was never asked about
 * marketing".
 */
export interface StoredVisitorConsent {
  v: 1
  /** Epoch ms of the decision (or of the implied default's recording). */
  at: number
  status: VisitorConsentStatus
  /** The category grant, derived from `status` at store time. */
  analytics: boolean
  /**
   * The advertising grant (AGL-1649). ABSENT on every record written before
   * the category existed, and absent reads as `false` — "was never asked"
   * is not "said yes". Reading it the other way would hand Google a basis
   * for every visitor who ever clicked Allow on an analytics-only banner.
   *
   * Carried by an explicit accept, and — since 2026-08-25 — by the opt-out
   * posture's implied default, on a site whose host turned the question on.
   * Never by a refusal: declined, opted out and GPC all deny it everywhere.
   * See {@link advertisingGrantedByStatus} for the order the policy and the
   * code move in.
   */
  advertising?: boolean
  /** ISO country at decision time, when known — the `implied,us` shape. */
  country?: string | null
}

/**
 * Keyed by hostId, not a bare name: preview deployments and localhost serve
 * many hosts from one origin, and one site's "yes" must never leak to
 * another's.
 */
export const VISITOR_CONSENT_STORAGE_PREFIX = 'aglyn:consent:'

/** The marketing runtime's cross-visit experiment id (`site-runtime.tsx`). */
export const VISITOR_ID_STORAGE_KEY = 'aglyn:visitor'

/** Fired on `window` whenever {@link storeVisitorConsent} records a state. */
export const VISITOR_CONSENT_CHANGED_EVENT = 'aglyn:consent:changed'

/**
 * Fired on `window` to open the privacy-choices panel — the change-your-mind
 * path, in BOTH directions. Sites can also link any element to
 * `#aglyn-consent`; the banner listens for those clicks.
 */
export const VISITOR_CONSENT_OPEN_EVENT = 'aglyn:consent:open'

export function visitorConsentStorageKey(hostId: string): string {
  return `${VISITOR_CONSENT_STORAGE_PREFIX}${hostId}`
}

/**
 * Cookie-name PREFIXES the analytics grant is answerable for (AGL-1606).
 *
 * A prefix sweep rather than a name list, because the names are derived and
 * configuration-dependent: `_ga` is joined by `_ga_<measurement id without the
 * `G-`>` per property, by `_gac_<id>` when Google Ads linking is on, and by
 * `_gid` where the legacy daily id is still issued. Hardcoding the two names a
 * given site happens to carry today is precisely how a withdrawal leaves an
 * identifier behind on the next site. `_ga` subsumes `_ga`, `_ga_*` and
 * `_gac_*`; `_gid` is the only Google analytics cookie that does not start
 * with it.
 */
export const ANALYTICS_COOKIE_PREFIXES: readonly string[] = ['_ga', '_gid']

/**
 * Cookie-name PREFIXES the ADVERTISING grant is answerable for (AGL-2486).
 *
 * These are Google's, and they are separate from the analytics list above for
 * a reason that is not tidiness: an advertising-only withdrawal must sweep
 * them while analytics keeps running. Folding them into
 * {@link ANALYTICS_COOKIE_PREFIXES} would have swept them on ANALYTICS
 * withdrawal and still not on the case that motivates this — leaving the same
 * hole, narrower — besides filing a conversion-linker cookie under a category
 * it does not belong to.
 *
 * `_gcl_*` is the conversion linker, written by gtag once `ad_storage` is
 * granted, and it is the one that started this: it does not begin with `_ga`,
 * so nothing swept it, on any path.
 *
 * ONE PREFIX, deliberately. `_gac_*` (Google Ads linking) was in this list
 * briefly and is not, because it is written under the ANALYTICS grant and is
 * already swept by the `_ga` prefix. Listing it here made every
 * analytics-only GRANT delete it — on a site that never asks the advertising
 * question `stored.advertising` is always false, so the sweep ran on the way
 * IN — which broke "cleanup is the withdrawal path only" (AGL-1606). Sweeping
 * wider is not free when a category nobody was asked about owns the trigger.
 *
 * PRECISION MATTERS, because {@link clearCookiesWithPrefixes} is `startsWith`.
 * `_gcl` cannot reach `_ga`, `_ga_<id>`, `_gac_<id>` or `_gid` — those are
 * analytics and MUST survive an advertising-only withdrawal.
 */
export const ADVERTISING_COOKIE_PREFIXES: readonly string[] = ['_gcl']

/**
 * Expire the advertising identifiers, on the same domain ladder as the
 * analytics sweep (AGL-2486).
 *
 * Deleting a cookie that was never set is a no-op; failing to delete one that
 * was is the defect — so this runs on every non-granting store rather than
 * only on a transition observed to be a withdrawal, which the record cannot
 * reliably tell us anyway.
 */
export function clearAdvertisingCookies(hostname?: string | null): string[] {
  return clearCookiesWithPrefixes(ADVERTISING_COOKIE_PREFIXES, hostname)
}

/**
 * Multi-label public suffixes: names under which registrations happen, so the
 * label to their left is the registrable one. A short list rather than the
 * Public Suffix List — the same trade `console-domains.ts` makes, and the
 * failure mode is mild in both directions: an unlisted suffix costs one
 * over-broad `domain=` write that the browser silently refuses (cookies cannot
 * be set on a public suffix), never a deletion on someone else's domain.
 */
const MULTI_LABEL_PUBLIC_SUFFIXES: ReadonlySet<string> = new Set([
  'co.uk', 'org.uk', 'me.uk', 'ac.uk', 'gov.uk',
  'com.au', 'net.au', 'org.au',
  'co.nz', 'co.za',
  'co.jp', 'ne.jp', 'or.jp',
  'com.br', 'com.mx', 'com.cn', 'com.tr',
  'co.in', 'co.kr', 'com.sg', 'com.hk',
])

function normalizeCookieHostname(hostname: string | null | undefined): string {
  return String(hostname ?? '')
    .trim()
    .toLowerCase()
    .replace(/\.+$/, '')
}

/**
 * The registrable domain of a hostname — the highest name a cookie may be
 * written at — or `null` when there is none.
 *
 * This is the fiddly half of the cleanup. GA's `cookie_domain: 'auto'` walks
 * UP from the hostname and writes at the highest domain the browser accepts,
 * which is the registrable domain with a leading dot: a visit to
 * `www.shop.acme.co.uk` leaves `_ga` on `.acme.co.uk`, not on the exact host.
 * A deletion aimed at the exact hostname therefore silently no-ops — it writes
 * a second, host-scoped, already-expired cookie and leaves the real one alone.
 *
 * `null` for a single-label host (`localhost`) and for IP literals: those take
 * host-only cookies, which the undecorated deletion already covers.
 */
export function registrableCookieDomain(
  hostname: string | null | undefined,
): string | null {
  // Named `name`, not `host`: `host.foo` inside app-utils is what the
  // AGL-1361 write-deny sweep reads as a host-document field, and a local
  // string's `.split`/`.includes` would land in it as phantom fields.
  const name = normalizeCookieHostname(hostname)
  if (!name) return null
  // IPv6 literal (bracketed or bare) and IPv4 literal: host-only cookies.
  if (name.startsWith('[') || name.includes(':')) return null
  if (/^\d+(\.\d+){3}$/.test(name)) return null
  const labels = name.split('.')
  if (labels.length < 2 || labels.some((label) => !label)) return null
  const lastTwo = labels.slice(-2).join('.')
  if (MULTI_LABEL_PUBLIC_SUFFIXES.has(lastTwo)) {
    // `acme.co.uk` is registrable; `co.uk` on its own is not a domain at all.
    return labels.length > 2 ? labels.slice(-3).join('.') : null
  }
  return lastTwo
}

/**
 * Every `domain=` value a cookie deletion has to try, most specific first and
 * stopping at the registrable domain.
 *
 * The whole ladder rather than the registrable domain alone because
 * `cookie_domain` is configurable: a host that pins it to `shop.acme.com`
 * leaves the cookie one rung below where `auto` would. Walking the rungs costs
 * a handful of no-op writes and removes the guess.
 *
 * Empty when there is no registrable domain — the host-only deletion that
 * {@link clearAnalyticsCookies} always performs is the complete answer there.
 */
export function analyticsCookieDomains(
  hostname: string | null | undefined,
): string[] {
  const name = normalizeCookieHostname(hostname)
  const registrable = registrableCookieDomain(name)
  if (!registrable) return []
  const domains: string[] = []
  let current = name
  for (;;) {
    domains.push(`.${current}`)
    if (current === registrable) break
    const cut = current.indexOf('.')
    if (cut < 0) break
    current = current.slice(cut + 1)
  }
  return domains
}

/**
 * Expire every analytics cookie this browser is carrying, at the host itself
 * and at every domain up to the registrable one. Returns the names it acted
 * on, which is what makes the cleanup assertable.
 *
 * Best-effort by construction: a cookie written at a path other than `/` is
 * not reachable this way, and `document.cookie` cannot report which domain a
 * cookie came from — hence the ladder. Neither limit applies to the cookies GA
 * actually writes (path `/`, registrable domain).
 */
export function clearAnalyticsCookies(
  hostname?: string | null,
): string[] {
  return clearCookiesWithPrefixes(ANALYTICS_COOKIE_PREFIXES, hostname)
}

/**
 * The same sweep for an ARBITRARY prefix set — the generalisation
 * {@link clearAnalyticsCookies} now delegates to.
 *
 * Extracted rather than copied because the hard part is not the prefix match,
 * it is {@link analyticsCookieDomains}: the ladder from the exact hostname up
 * to the registrable domain, which exists because a tag written with an `auto`
 * cookie domain leaves its identifier one or more rungs ABOVE the host. A
 * second vendor's sweep re-derived by hand would get that wrong the first time
 * and pass its own test, because a deletion aimed at the wrong domain silently
 * no-ops — it writes an already-expired host-scoped cookie and reports success.
 * One implementation, one ladder, one place to fix.
 *
 * Meta's `_fbp`/`_fbc` are written exactly like `_ga`: path `/`, registrable
 * domain, first-party. Nothing here is Google-specific and nothing here is
 * Meta-specific.
 */
export function clearCookiesWithPrefixes(
  prefixes: readonly string[],
  hostname?: string | null,
): string[] {
  if (typeof document === 'undefined') return []
  // An empty prefix set would match EVERY cookie via `''.startsWith`, which
  // would delete the cart, the member session and the consent record itself.
  // A caller with nothing to sweep gets nothing swept.
  if (!prefixes || prefixes.length === 0) return []
  try {
    const names = new Set<string>()
    for (const pair of String(document.cookie ?? '').split(';')) {
      const name = pair.split('=')[0].trim()
      if (name && prefixes.some((p) => !!p && name.startsWith(p))) {
        names.add(name)
      }
    }
    if (names.size === 0) return []
    const domains = analyticsCookieDomains(
      hostname ??
        (typeof window !== 'undefined' ? window.location?.hostname : ''),
    )
    for (const name of names) {
      const expired = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
      document.cookie = expired
      for (const domain of domains) {
        document.cookie = `${expired}; domain=${domain}`
      }
    }
    return [...names]
  } catch {
    // Cookies disabled or a hostile `document.cookie`: the gate still keeps
    // the script out, which is the load-bearing half.
    return []
  }
}

/** A single consent-mode signal value. */
export type AnalyticsConsentSignal = 'granted' | 'denied'

/**
 * The consent-mode signal set this tool is answerable for.
 *
 * `ad_storage` and friends are typed as the literal `'denied'`, not as
 * {@link AnalyticsConsentSignal}: the tool asks a visitor about analytics and
 * nothing else, so there is no advertising basis on file to grant, in either
 * direction. Widening them is a change the type has to be edited to allow.
 */
export interface AnalyticsConsentSignals {
  analytics_storage: AnalyticsConsentSignal
  ad_storage: 'denied'
  ad_user_data: 'denied'
  ad_personalization: 'denied'
}

/**
 * The consent-mode payload for a given analytics grant — ONE source, so the
 * `default` declared before the tag loads (AGL-1622) and the `update` sent on a
 * mid-pageview withdrawal (AGL-1608) can never drift apart. Two hand-written
 * literals that disagree is how a tag ends up believing a state the visitor
 * never chose.
 */
export function analyticsConsentSignals(
  granted: boolean,
): AnalyticsConsentSignals {
  return {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  }
}

/** The four signals when advertising is in play (AGL-1649). */
export interface VisitorConsentSignals {
  analytics_storage: AnalyticsConsentSignal
  ad_storage: AnalyticsConsentSignal
  ad_user_data: AnalyticsConsentSignal
  ad_personalization: AnalyticsConsentSignal
}

/**
 * The consent-mode payload for a pair of category grants (AGL-1649).
 *
 * A SEPARATE function from {@link analyticsConsentSignals} rather than a
 * widening of it, deliberately. AGL-1649 says not to widen those three
 * literals, and the reason survives this change: a caller that only knows
 * about analytics should be unable to express a grant it has no answer for.
 * Reaching an advertising grant now requires calling something else and
 * passing it, which is a thing a reviewer can see in a diff.
 *
 * Advertising is clamped to analytics. `ad_storage: 'granted'` with
 * `analytics_storage: 'denied'` is not a state this tool can reach honestly —
 * every refusal path (declined, opted out, GPC) withdraws both together — so
 * the clamp makes the impossible state unrepresentable rather than merely
 * unreached.
 */
export function consentModeSignals(grants: {
  analytics: boolean
  advertising: boolean
}): VisitorConsentSignals {
  const analytics = grants.analytics === true
  const advertising = analytics && grants.advertising === true
  const ads: AnalyticsConsentSignal = advertising ? 'granted' : 'denied'
  return {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  }
}

/**
 * Whether advertising storage may run for this visitor on this site.
 *
 * THREE independent conditions, and all of them are load-bearing:
 *
 * 1. The host asks about advertising at all. A record that says yes is
 *    ignored on a site that never turned the category on — which covers a
 *    host switching it back off, and covers a stale record generally.
 * 2. The record's status can carry a grant — an explicit accept, and nothing
 *    else (see {@link advertisingGrantedByStatus}). An `implied` default
 *    cannot, and every refusal status refuses.
 * 3. The record actually says yes to this category.
 *
 * Absent, unreadable or legacy records all answer `false`, which is the same
 * answer they gave before the category existed.
 */
export function advertisingGrantedByRecord(
  host: VisitorConsentHost | null | undefined,
  stored: StoredVisitorConsent | null | undefined,
): boolean {
  if (!hostAsksAboutAdvertising(host)) return false
  if (!stored) return false
  if (!advertisingGrantedByStatus(stored.status)) return false
  return stored.advertising === true
}

/**
 * Make every resident analytics tag agree with the consent state, and return
 * the ids it acted on (AGL-1608).
 *
 * The AGL-1498 gate stops a tag from LOADING, which is the right enforcement
 * for a fresh pageview and the wrong tool for a visitor who withdraws
 * mid-pageview: the vendor's library has already executed, and React
 * unmounting its `<script>` element does not unload it. So the tag that is
 * already there is told, through the adapter of whichever vendor it is
 * (`analytics-provider.ts`). A tag only exists after a grant, so this never
 * touches the gate.
 *
 * Symmetric on purpose: a visitor who opts out and changes their mind in the
 * same pageview would otherwise stay silently unmeasured until they
 * navigated. The re-grant restores whatever the CALLER passes — analytics
 * alone on a site that never asks the advertising question (`advertising`
 * defaults to false), and both categories where the visitor's record grants
 * both (AGL-1649). Advertising is clamped to analytics, so a withdrawal can
 * never restore advertising alone.
 */
export function setResidentAnalyticsTags(
  granted: boolean,
  advertising = false,
): string[] {
  if (typeof window === 'undefined') return []
  return applyAnalyticsConsent({ analytics: granted, advertising })
}

/**
 * The stored record, or null when there is none (never resolved, storage
 * unavailable, or an unrecognized shape — treated alike: not yet decided).
 */
export function readStoredVisitorConsent(
  hostId: string | null | undefined,
): StoredVisitorConsent | null {
  if (!hostId || typeof window === 'undefined') return null
  try {
    return parseStoredVisitorConsent(
      window.localStorage.getItem(visitorConsentStorageKey(hostId)),
    )
  } catch {
    // No storage (privacy mode) — the visitor is simply undecided, which is
    // the safe reading.
    return null
  }
}

/**
 * A stored record's text as the record it may claim to be, or null.
 *
 * The one validation every copy of a record passes through — the per-origin
 * store above, and the `aglyn_consent` mirror a server reads off a request
 * (AGL-3289) — so a copy read anywhere grants exactly what its status
 * supports and nothing a hand edit added.
 */
export function parseStoredVisitorConsent(
  raw: string | null | undefined,
): StoredVisitorConsent | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (
      parsed?.v !== 1 ||
      typeof parsed.status !== 'string' ||
      !CONSENT_STATUSES.has(parsed.status)
    ) {
      return null
    }
    const status = parsed.status as VisitorConsentStatus
    return {
      v: 1,
      at: typeof parsed.at === 'number' ? parsed.at : 0,
      status,
      // Derived, never trusted from storage: a hand-edited record must not
      // grant what its status does not.
      analytics: analyticsGrantedByStatus(status),
      // Same discipline for advertising (AGL-1649), plus one more clause:
      // the key has to be PRESENT and true. Absent is a record written
      // before the category existed — never asked, which is not a yes.
      advertising:
        advertisingGrantedByStatus(status) && parsed.advertising === true,
      country: typeof parsed.country === 'string' ? parsed.country : null,
    }
  } catch {
    // Corrupt JSON — the visitor is simply undecided, which is the safe
    // reading.
    return null
  }
}

/**
 * Record a consent state and make the page agree with it immediately.
 *
 * Storing the record is itself strictly-necessary storage (it is how a "no"
 * is remembered), so it is exempt from the consent it records. Any
 * non-granting state also REMOVES the identifiers the grant allowed —
 * the persistent `aglyn:visitor` id AND the GA cookies
 * ({@link clearAnalyticsCookies}): opting out cleans up, it does not merely
 * stop adding (AGL-1606) — and SILENCES the tag that is already running
 * ({@link setResidentAnalyticsTags}), without which the cleanup un-does itself
 * within the same pageview (AGL-1608). Listeners hear about it via
 * {@link VISITOR_CONSENT_CHANGED_EVENT} so the GA gate and the runtime react
 * without a reload.
 *
 * The two cleanups are deliberately in SEPARATE try blocks: private mode
 * throws on `localStorage` while cookies still work, and a withdrawal must
 * not lose the cookie sweep to that.
 */
export function storeVisitorConsent(
  hostId: string,
  state: {
    status: VisitorConsentStatus
    country?: string | null
    /**
     * The visitor's answer to the advertising question (AGL-1649). Omitted
     * by every caller that does not ask one, and omitted means NO.
     */
    advertising?: boolean
  },
): StoredVisitorConsent {
  const stored: StoredVisitorConsent = {
    v: 1,
    at: Date.now(),
    status: state.status,
    analytics: analyticsGrantedByStatus(state.status),
    // Derived at store time exactly as `analytics` is, so a caller cannot
    // persist a grant the status does not support — a refusal always writes
    // `false` here even if the panel's checkbox was left ticked.
    advertising:
      advertisingGrantedByStatus(state.status) && state.advertising === true,
    country: state.country ?? null,
  }
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(
        visitorConsentStorageKey(hostId),
        JSON.stringify(stored),
      )
      if (!stored.analytics) {
        window.localStorage.removeItem(VISITOR_ID_STORAGE_KEY)
      }
    } catch {
      // Private mode: the state holds for this page via the event below,
      // and is re-derived next visit — the safe failure.
    }
    // Silence the RESIDENT tag before sweeping, never after (AGL-1608): the
    // gate only keeps the script from LOADING, so a mid-pageview withdrawal
    // leaves gtag.js live and it re-writes `_ga_<id>` on its next enhanced
    // measurement event. Sweeping first would delete three cookies and
    // immediately get one back.
    setResidentAnalyticsTags(stored.analytics, stored.advertising === true)
    if (!stored.analytics) {
      clearAnalyticsCookies()
    }
    // The advertising half, and it belongs HERE rather than only in
    // `revokeAdvertisingTags` (AGL-2486). That function sweeps a vendor whose
    // marked script elements are in the document, and it is installed only on
    // our own marketing host — so on a CUSTOMER site, where the advertising
    // question is equally available, withdrawing advertising ran no sweep at
    // all. `_gcl_au` is written by the GA tag `site-analytics.tsx` mounts, not
    // by a script this module marked, so no element-scoped teardown was ever
    // going to reach it on any surface.
    //
    // This path is the one every site shares: it is where a decision becomes a
    // record. Unconditional on the grant being absent, for the reason in
    // `clearAdvertisingCookies` — a delete of something never set costs
    // nothing, a missed delete is the defect.
    if (!stored.advertising) {
      clearAdvertisingCookies()
    }
    try {
      window.dispatchEvent(new CustomEvent(VISITOR_CONSENT_CHANGED_EVENT))
    } catch {
      // A dispatch failure only delays consumers until the next read.
    }
  }
  return stored
}

/**
 * The GA gate's whole verdict: may the analytics category run for this
 * visitor, on this site, right now?
 *
 * - Consent machinery not required (no gated features, or host opted out of
 *   the tool) → yes.
 * - Required and the recorded state grants analytics (`accepted` or
 *   `implied`) → yes.
 * - Required and anything else — declined, opted out, GPC, or NOT YET
 *   RESOLVED → no. Undecided is not a maybe: the script stays out until a
 *   state that grants it exists. The gate never asks WHY consent is absent,
 *   which is what keeps it posture-independent.
 */
export function isAnalyticsAllowed(
  host: VisitorConsentHost | null | undefined,
  stored: StoredVisitorConsent | null,
): boolean {
  if (!hostConsentRequired(host)) return true
  return stored?.analytics === true
}

/**
 * Where the marketing runtime may keep its experiment visitor id.
 *
 * `local` — the id persists across visits. `session` — the id lives in
 * sessionStorage: variants stay stable within the visit and the identifier
 * dies with the tab. The downgrade applies while the consent machinery is
 * active for this site and analytics is not granted; the id itself never
 * leaves the browser either way (the experiment beacons are aggregate — no
 * visitor id on the wire).
 */
export function resolveVisitorIdPersistence(
  host: VisitorConsentHost | null | undefined,
  stored: StoredVisitorConsent | null,
): 'local' | 'session' {
  return isAnalyticsAllowed(host, stored) ? 'local' : 'session'
}
